<template>
  <div>
    <helpers-section-header class="mt-6 mb-2"> WhaleIndex Signal Rules </helpers-section-header>
    <p class="text-body-2 mb-4">
      WhaleIndex portfolios are curated model portfolios constructed from 13F institutional filing
      data. Each index represents a specific strategy — for example, the top-ranked managers by
      WhalScore, the most concentrated positions across hedge funds, or stocks with the highest
      institutional accumulation. The backtester generates buy signals when it rebalances to match
      the index composition each quarter. Enable one or more indices below.
    </p>

    <helpers-section-sub-header class="mt-6 mb-2"> Signal Evaluation Window </helpers-section-sub-header>
    <p class="text-body-2 mb-2">
      How many days after a WhaleIndex rebalance to continue treating new stocks in the index as
      buy candidates. A longer window allows the signal to remain active across the quarter.
    </p>
    <v-row density="compact">
      <v-col v-if="!appcriteria.general.screener" cols="12">
        <v-text-field
          v-model="appcriteria.signals.whaleindex_signal.number_of_days_to_evaluate"
          label="Days to keep signal active after rebalance"
          suffix="days"
          variant="outlined"
          density="compact"
          hint="Number of days after rebalance to continue evaluating as a buy. Max 100."
          persistent-hint
          style="max-width: 250px"
        />
      </v-col>
    </v-row>

    <v-col cols="12" mb-2>
      <helpers-section-sub-header>Trading Delay</helpers-section-sub-header>
      <p class="text-body-2 mb-2">
        Days to wait after the 13F deadline before acting on the index rebalance. Accounts for the
        time needed to process and publish updated index compositions.
      </p>
      <v-row density="compact">
        <v-col cols="12" sm="6" md="4" xl="3">
          <v-text-field
            v-model="appcriteria.signals.whaleindex_signal.trading_delay"
            type="number"
            suffix="days"
            variant="outlined"
            density="compact"
            hint="Days after 13F deadline before being considered for backtesting"
            persistent-hint
            class="pt-0"
          />
        </v-col>
      </v-row>
    </v-col>

    <v-divider class="my-4" />
    <helpers-section-sub-header class="mb-2">Equal-Weight Clone Mode</helpers-section-sub-header>
    <p class="text-body-2 mb-3">
      When enabled, the backtester replicates the selected index exactly each quarter: stocks dropped
      from the index are sold on the rebalance date, new additions are bought, and all positions are
      trued up to equal weight (1/N of total portfolio). Stop loss and profit target rules still apply
      intra-quarter. Only one index may be selected in this mode.
    </p>
    <v-checkbox
      v-model="appcriteria.signals.whaleindex_signal.clone_mode"
      label="Enable equal-weight quarterly rebalancing (clone index)"
      color="primary"
      hide-details
      class="mb-4"
    />

    <v-table class="my-8">
      <thead>
        <tr>
          <th>WhaleIndex Portfolio</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="m in appcriteria.signals.whaleindex_signal.whale_indices"
          :key="m.whale_index_id"
        >
          <td class="py-4">
            <v-checkbox
              v-model="m.enabled"
              :label="m.name"
              color="primary"
              hide-details
              class="mt-0"
              @update:model-value="onIndexToggle(m)"
            />
          </td>
        </tr>
      </tbody>
    </v-table>
  </div>
</template>

<script>
// Modules
import { mapFields } from '@/store/helpers'

export default {
  data() {
    return {
      dialog: false
    }
  },
  computed: {
    ...mapFields({
      appcriteria: 'app.whaleTraderCriteria',
      criteria: 'app.whaleTraderCriteria.signals.whaleindex_signal',
      enabled: 'app.whaleTraderCriteria.signals.whaleindex_signal.enabled',
      signalView: 'app.signalView'
    })
  },
  methods: {
    // In clone mode, enabling one index disables all others (radio-button behavior)
    onIndexToggle(toggled) {
      if (this.criteria.clone_mode && toggled.enabled) {
        this.criteria.whale_indices.forEach(m => {
          if (m !== toggled) m.enabled = false
        })
      }
    }
  }
}
</script>
